import { CONSTANTS } from '../_data/lookups.js';
function isMutableMacroStepKey(stepKey) {
    return stepKey.startsWith('BRANCH_STEP-')
        || stepKey.startsWith('WHILE_STEP-')
        || stepKey.startsWith('FOR_STEP-');
}
function stripMutableCase(stepCase) {
    const { when, what, ...rest } = stepCase;
    const { stepKey: _whenStepKey, ...canonicalWhen } = when;
    const { stepKey: _whatStepKey, ...canonicalWhat } = what;
    return {
        ...rest,
        when: canonicalWhen,
        what: canonicalWhat,
    };
}
function stripMutableLoopCase(stepCase) {
    const { when, what, ...rest } = stepCase;
    const { stepKey: _whenStepKey, ...canonicalWhen } = when;
    const { stepKey: _whatStepKey, ...canonicalWhat } = what;
    return {
        ...rest,
        when: canonicalWhen,
        what: canonicalWhat,
    };
}
function buildStepPathByStepKeyMap(workflow) {
    const stepPathByStepKey = new Map();
    const registerStep = (step, pathPrefix) => {
        switch (step.stepKind) {
            case CONSTANTS.Enums.StepKind.tool:
                stepPathByStepKey.set(step.stepKey, pathPrefix);
                return;
            case CONSTANTS.Enums.StepKind.branch:
                step.cases.forEach((stepCase, caseIndex) => {
                    stepPathByStepKey.set(stepCase.when.stepKey, `${pathPrefix}/cases/${caseIndex}/when`);
                    stepPathByStepKey.set(stepCase.what.stepKey, `${pathPrefix}/cases/${caseIndex}/what`);
                });
                return;
            case CONSTANTS.Enums.StepKind.while:
            case CONSTANTS.Enums.StepKind.for:
                stepPathByStepKey.set(step.case.when.stepKey, `${pathPrefix}/case/when`);
                stepPathByStepKey.set(step.case.what.stepKey, `${pathPrefix}/case/what`);
                return;
            default:
                throw new Error('Unsupported stepKind while projecting workflow.');
        }
    };
    workflow.stepsByThreadIndex.forEach((steps, threadIndex) => {
        steps.forEach((step, stepIndex) => {
            registerStep(step, `/threads/${threadIndex}/steps/${stepIndex}`);
        });
    });
    return stepPathByStepKey;
}
function toCanonicalWorkflowStateEntry(entry, stepPathByStepKey) {
    if (entry.workflowStateEntryKind !== 'internalInputPotential') {
        return entry;
    }
    const sourceStepPath = stepPathByStepKey.get(entry.toolStepRoleAddress.stepKey);
    if (!sourceStepPath) {
        throw new Error(`Cannot project workflow state: no execution path found for stepKey '${entry.toolStepRoleAddress.stepKey}'.`);
    }
    return {
        workflowStateEntryKind: 'internalInputPotential',
        toolStepRoleAddress: {
            stepPath: sourceStepPath,
            roleName: entry.toolStepRoleAddress.roleName,
        },
    };
}
export function mutableStepToStep(step) {
    switch (step.stepKind) {
        case CONSTANTS.Enums.StepKind.tool: {
            const { stepKey: _stepKey, ...canonicalToolStep } = step;
            return canonicalToolStep;
        }
        case CONSTANTS.Enums.StepKind.branch: {
            const { stepKey: _stepKey, cases, ...rest } = step;
            return {
                ...rest,
                cases: cases.map(stripMutableCase),
            };
        }
        case CONSTANTS.Enums.StepKind.while:
        case CONSTANTS.Enums.StepKind.for: {
            const { stepKey: _stepKey, case: stepCase, ...rest } = step;
            return {
                ...rest,
                case: stripMutableLoopCase(stepCase),
            };
        }
        default:
            throw new Error('Unsupported stepKind while projecting steps.');
    }
}
function toMutableWorkflowStateEntry(entry, resolveStepKeyByPath) {
    if (entry.workflowStateEntryKind !== 'internalInputPotential') {
        return entry;
    }
    const sourceToolStepKey = resolveStepKeyByPath(entry.toolStepRoleAddress.stepPath);
    if (!sourceToolStepKey) {
        throw new Error(`Cannot deproject workflow state: no mutable key found for stepPath '${entry.toolStepRoleAddress.stepPath}'.`);
    }
    return {
        workflowStateEntryKind: 'internalInputPotential',
        toolStepRoleAddress: {
            stepKey: sourceToolStepKey,
            roleName: entry.toolStepRoleAddress.roleName,
        },
    };
}
function toMutableToolStep(step, generateIdentifier, stepKeyOverride) {
    const key = stepKeyOverride ?? generateIdentifier('ToolStepKey');
    return {
        ...step,
        stepKey: key,
    };
}
function toMutableCase(stepCase, generateIdentifier) {
    const { when, what, ...rest } = stepCase;
    return {
        ...rest,
        when: toMutableToolStep(when, generateIdentifier),
        what: toMutableToolStep(what, generateIdentifier),
    };
}
function toMutableLoopCase(stepCase, generateIdentifier) {
    const { when, what, ...rest } = stepCase;
    return {
        ...rest,
        when: toMutableToolStep(when, generateIdentifier),
        what: toMutableToolStep(what, generateIdentifier),
    };
}
function toMutableBranchStep(step, generateIdentifier) {
    const { cases, ...rest } = step;
    const key = generateIdentifier('BranchStepKey');
    return {
        ...rest,
        stepKey: key,
        cases: cases.map((stepCase) => toMutableCase(stepCase, generateIdentifier)),
    };
}
function toMutableWhileStep(step, generateIdentifier) {
    const { case: stepCase, ...rest } = step;
    const key = generateIdentifier('WhileStepKey');
    return {
        ...rest,
        stepKey: key,
        case: toMutableLoopCase(stepCase, generateIdentifier),
    };
}
function toMutableForStep(step, generateIdentifier) {
    const { case: stepCase, ...rest } = step;
    const key = generateIdentifier('ForStepKey');
    return {
        ...rest,
        stepKey: key,
        case: toMutableLoopCase(stepCase, generateIdentifier),
    };
}
function toMutableStep(step, generateIdentifier) {
    switch (step.stepKind) {
        case CONSTANTS.Enums.StepKind.tool:
            return toMutableToolStep(step, generateIdentifier);
        case CONSTANTS.Enums.StepKind.branch:
            return toMutableBranchStep(step, generateIdentifier);
        case CONSTANTS.Enums.StepKind.while:
            return toMutableWhileStep(step, generateIdentifier);
        case CONSTANTS.Enums.StepKind.for:
            return toMutableForStep(step, generateIdentifier);
        default:
            throw new Error('Unsupported stepKind while deprojecting steps.');
    }
}
function buildStepKeyByStepPathMap(workflow) {
    const stepPathByStepKey = buildStepPathByStepKeyMap(workflow);
    const stepKeyByStepPath = new Map();
    for (const [stepKey, stepPath] of stepPathByStepKey.entries()) {
        stepKeyByStepPath.set(stepPath, stepKey);
    }
    return stepKeyByStepPath;
}
function buildStepKeyByStepPathMapAllowDuplicateKeys(workflow) {
    const out = new Map();
    const registerStep = (step, pathPrefix) => {
        out.set(pathPrefix, step.stepKey);
        switch (step.stepKind) {
            case CONSTANTS.Enums.StepKind.tool:
                return;
            case CONSTANTS.Enums.StepKind.branch:
                step.cases.forEach((stepCase, caseIndex) => {
                    out.set(`${pathPrefix}/cases/${caseIndex}/when`, stepCase.when.stepKey);
                    out.set(`${pathPrefix}/cases/${caseIndex}/what`, stepCase.what.stepKey);
                });
                return;
            case CONSTANTS.Enums.StepKind.while:
            case CONSTANTS.Enums.StepKind.for:
                out.set(`${pathPrefix}/case/when`, step.case.when.stepKey);
                out.set(`${pathPrefix}/case/what`, step.case.what.stepKey);
                return;
            default:
                throw new Error('Unsupported stepKind while building stepKey lookup map.');
        }
    };
    workflow.stepsByThreadIndex.forEach((steps, threadIndex) => {
        steps.forEach((step, stepIndex) => {
            registerStep(step, `/threads/${threadIndex}/steps/${stepIndex}`);
        });
    });
    return out;
}
function buildToolStepKeyByStepPathMapAllowDuplicateKeys(workflow) {
    const out = new Map();
    const registerStep = (step, pathPrefix) => {
        switch (step.stepKind) {
            case CONSTANTS.Enums.StepKind.tool:
                out.set(pathPrefix, step.stepKey);
                return;
            case CONSTANTS.Enums.StepKind.branch:
                step.cases.forEach((stepCase, caseIndex) => {
                    out.set(`${pathPrefix}/cases/${caseIndex}/when`, stepCase.when.stepKey);
                    out.set(`${pathPrefix}/cases/${caseIndex}/what`, stepCase.what.stepKey);
                });
                return;
            case CONSTANTS.Enums.StepKind.while:
            case CONSTANTS.Enums.StepKind.for:
                out.set(`${pathPrefix}/case/when`, step.case.when.stepKey);
                out.set(`${pathPrefix}/case/what`, step.case.what.stepKey);
                return;
            default:
                throw new Error('Unsupported stepKind while building stepKey lookup map.');
        }
    };
    workflow.stepsByThreadIndex.forEach((steps, threadIndex) => {
        steps.forEach((step, stepIndex) => {
            registerStep(step, `/threads/${threadIndex}/steps/${stepIndex}`);
        });
    });
    return out;
}
function resolveStepKeyWithRelocations(stepPath, stepKeyByStepPath, pathRelocations) {
    const direct = stepKeyByStepPath.get(stepPath);
    if (direct) {
        return direct;
    }
    for (const relocation of pathRelocations) {
        if (relocation.fromStepPath === stepPath) {
            const relocated = stepKeyByStepPath.get(relocation.toStepPath);
            if (relocated) {
                return relocated;
            }
        }
        if (relocation.toStepPath === stepPath) {
            const relocated = stepKeyByStepPath.get(relocation.fromStepPath);
            if (relocated) {
                return relocated;
            }
        }
    }
    return undefined;
}
export function workflowToMutableWorkflow(workflow, generateIdentifier) {
    const stepsByThreadIndex = workflow.stepsByThreadIndex.map((steps) => steps.map((step) => toMutableStep(step, generateIdentifier)));
    const seededMutableWorkflow = {
        ...workflow,
        stepsByThreadIndex,
        workflowState: {},
    };
    const stepKeyByStepPath = buildStepKeyByStepPathMap(seededMutableWorkflow);
    const workflowState = {};
    for (const [stepPath, inputEntryByRoleName] of Object.entries(workflow.workflowState)) {
        const stepKey = stepKeyByStepPath.get(stepPath);
        if (!stepKey) {
            throw new Error(`Cannot deproject workflow state: no mutable key found for stepPath '${stepPath}'.`);
        }
        workflowState[stepKey] = Object.fromEntries(Object.entries(inputEntryByRoleName).map(([roleName, entry]) => {
            if (entry === undefined) {
                throw new Error(`Cannot deproject workflow state: role '${roleName}' on stepPath '${stepPath}' is explicitly undefined.`);
            }
            return [
                roleName,
                toMutableWorkflowStateEntry(entry, (stepPath) => stepKeyByStepPath.get(stepPath)),
            ];
        }));
    }
    return {
        ...workflow,
        stepsByThreadIndex,
        workflowState,
    };
}
export function mutableWorkflowToWorkflow(mutableWorkflow) {
    const stepPathByStepKey = buildStepPathByStepKeyMap(mutableWorkflow);
    const workflowState = {};
    for (const [stepKey, inputEntryByRoleName] of Object.entries(mutableWorkflow.workflowState)) {
        if (!inputEntryByRoleName) {
            continue;
        }
        const stepPath = stepPathByStepKey.get(stepKey);
        if (!stepPath) {
            throw new Error(`Cannot project workflow state: no execution path found for stepKey '${stepKey}'.`);
        }
        workflowState[stepPath] = Object.fromEntries(Object.entries(inputEntryByRoleName).map(([roleName, entry]) => {
            if (entry === undefined) {
                throw new Error(`Cannot project workflow state: role '${roleName}' on stepKey '${stepKey}' is explicitly undefined.`);
            }
            return [
                roleName,
                toCanonicalWorkflowStateEntry(entry, stepPathByStepKey),
            ];
        }));
    }
    return {
        ...mutableWorkflow,
        stepsByThreadIndex: mutableWorkflow.stepsByThreadIndex.map((steps) => steps.map(mutableStepToStep)),
        workflowState,
    };
}
export function mutableStepKeyToStepPath(mutableWorkflow, stepKey) {
    const stepPathByStepKey = buildStepPathByStepKeyMap(mutableWorkflow);
    const stepPath = stepPathByStepKey.get(stepKey);
    if (!stepPath) {
        throw new Error(`Cannot project stepPath: no execution path found for stepKey '${stepKey}'.`);
    }
    return stepPath;
}
export function projectMutableWorkflowStateEntriesByStepKeyToCanonicalDelta(mutableWorkflow, stepKey, entries) {
    const stepPathByStepKey = buildStepPathByStepKeyMap(mutableWorkflow);
    const stepPath = stepPathByStepKey.get(stepKey);
    if (!stepPath) {
        throw new Error(`Cannot project workflow state delta: no execution path found for stepKey '${stepKey}'.`);
    }
    const canonicalEntries = Object.fromEntries(Object.entries(entries)
        .filter(([, entry]) => entry !== undefined)
        .map(([roleName, entry]) => {
        if (entry === undefined) {
            throw new Error(`Cannot project workflow state delta: role '${roleName}' on stepKey '${stepKey}' is explicitly undefined.`);
        }
        return [
            roleName,
            toCanonicalWorkflowStateEntry(entry, stepPathByStepKey),
        ];
    }));
    return {
        [stepPath]: canonicalEntries,
    };
}
/**
 * Converts a canonical WorkflowTrace to a MutableWorkflowTrace.
 *
 * This processes the trace incrementally, maintaining a running MutableWorkflow
 * to ensure accurate step key mapping for each delta's workflowStateDelta.
 *
 * - Initial workflow_start is converted to MutableWorkflow
 * - Each tick's insertedSteps are converted to MutableStep
 * - Each tick's workflowStateDelta keys are converted from StepPath to MutableToolStepKey
 * - The workflow is evolved tick-by-tick to maintain correct mappings
 */
export function canonicalWorkflowTraceToMutableWorkflowTrace(trace, generateIdentifier) {
    const tracePoints = [];
    // Find and convert workflow_start
    let currentMutableWorkflow = null;
    for (const tracePoint of trace.tracePoints) {
        if (tracePoint.tracePointKind === 'workflow_start') {
            const workflow = 'workflow' in tracePoint && tracePoint.workflow ? tracePoint.workflow : null;
            if (workflow) {
                currentMutableWorkflow = workflowToMutableWorkflow(workflow, generateIdentifier);
                tracePoints.push({
                    ...tracePoint,
                    workflow: currentMutableWorkflow,
                });
            }
            break;
        }
    }
    if (!currentMutableWorkflow) {
        throw new Error('WorkflowTrace missing workflow_start with valid workflow');
    }
    // Process tick points and evolve the workflow
    for (const tracePoint of trace.tracePoints) {
        if (tracePoint.tracePointKind !== 'tick') {
            if (tracePoint.tracePointKind === 'workflow_start') {
                // Already added
                continue;
            }
            if (tracePoint.tracePointKind === 'workflow_end') {
                tracePoints.push(tracePoint);
            }
            // Skip suspended points (not part of mutable trace contract)
            continue;
        }
        const tick = tracePoint;
        const pathRelocations = (tick.delta?.pathRelocations ?? []);
        const currentToolStepKeyByStepPath = buildToolStepKeyByStepPathMapAllowDuplicateKeys(currentMutableWorkflow);
        const resolveCurrentStepKey = (stepPath) => resolveStepKeyWithRelocations(stepPath, currentToolStepKeyByStepPath, pathRelocations);
        // Convert insertedSteps if present
        let nextStepsByThreadIndexFromMutation = null;
        const convertedInsertedSteps = tick.delta?.stepsMutation?.insertedSteps
            ? tick.delta.stepsMutation.insertedSteps.map((step, insertedOffset) => {
                if (step.stepKind !== CONSTANTS.Enums.StepKind.tool ||
                    tick.delta?.stepsMutation?.insertAt === undefined) {
                    return toMutableStep(step, generateIdentifier);
                }
                const insertedPath = `/threads/${tick.threadIndex}/steps/${tick.delta.stepsMutation.insertAt + insertedOffset}`;
                let reusedStepKey;
                for (const relocation of pathRelocations) {
                    if (relocation.toStepPath === insertedPath) {
                        reusedStepKey = resolveCurrentStepKey(relocation.fromStepPath);
                        if (reusedStepKey) {
                            break;
                        }
                    }
                    if (relocation.fromStepPath === insertedPath) {
                        reusedStepKey = resolveCurrentStepKey(relocation.toStepPath);
                        if (reusedStepKey) {
                            break;
                        }
                    }
                }
                return toMutableToolStep(step, generateIdentifier, reusedStepKey);
            })
            : undefined;
        if (convertedInsertedSteps && tick.delta?.stepsMutation?.insertAt !== undefined) {
            const threadIndex = tick.threadIndex;
            const threadSteps = currentMutableWorkflow.stepsByThreadIndex[threadIndex] ?? [];
            const insertAt = tick.delta.stepsMutation.insertAt;
            nextStepsByThreadIndexFromMutation = [
                ...currentMutableWorkflow.stepsByThreadIndex.slice(0, threadIndex),
                [
                    ...threadSteps.slice(0, insertAt),
                    ...convertedInsertedSteps,
                    ...threadSteps.slice(insertAt),
                ],
                ...currentMutableWorkflow.stepsByThreadIndex.slice(threadIndex + 1),
            ];
        }
        const workflowShapeForTick = nextStepsByThreadIndexFromMutation
            ? {
                ...currentMutableWorkflow,
                stepsByThreadIndex: nextStepsByThreadIndexFromMutation,
            }
            : currentMutableWorkflow;
        const stepKeyByStepPathForTick = buildStepKeyByStepPathMapAllowDuplicateKeys(workflowShapeForTick);
        const toolStepKeyByStepPathForTick = buildToolStepKeyByStepPathMapAllowDuplicateKeys(workflowShapeForTick);
        const resolveStepKeyForTick = (stepPath) => resolveStepKeyWithRelocations(stepPath, stepKeyByStepPathForTick, pathRelocations);
        const resolveToolStepKeyForTick = (stepPath) => resolveStepKeyWithRelocations(stepPath, toolStepKeyByStepPathForTick, pathRelocations);
        // Convert workflowStateDelta keys from paths to stepKeys
        const convertedWorkflowStateDelta = tick.delta?.workflowStateDelta
            ? Object.fromEntries(Object.entries(tick.delta.workflowStateDelta).map(([stepPath, roleEntries]) => {
                const stepKey = resolveToolStepKeyForTick(stepPath);
                if (!stepKey) {
                    throw new Error(`Cannot convert trace: no mutable stepKey found for canonical stepPath '${stepPath}' at tick ${tick.tracePointIndex}`);
                }
                // Convert internalInputPotential references in role entries
                const convertedRoleEntries = {};
                for (const [roleName, entry] of Object.entries(roleEntries)) {
                    convertedRoleEntries[roleName] = toMutableWorkflowStateEntry(entry, resolveToolStepKeyForTick);
                }
                return [stepKey, convertedRoleEntries];
            }))
            : undefined;
        // Build mutable delta, omitting undefined properties
        const mutableDeltaBuilder = {
            suspendedData: tick.delta?.suspendedData,
        };
        if (convertedInsertedSteps && tick.delta?.stepsMutation?.insertAt !== undefined) {
            mutableDeltaBuilder.stepsMutation = {
                insertAt: tick.delta.stepsMutation.insertAt,
                insertedSteps: convertedInsertedSteps,
            };
        }
        if (convertedWorkflowStateDelta) {
            mutableDeltaBuilder.workflowStateDelta = convertedWorkflowStateDelta;
        }
        const mutableDelta = mutableDeltaBuilder;
        const tickStepKey = resolveStepKeyForTick(tick.stepPath);
        if (!tickStepKey) {
            throw new Error(`Cannot convert trace: no mutable stepKey found for tick.stepPath '${tick.stepPath}' at tick ${tick.tracePointIndex}`);
        }
        const mutableMacroStepContext = (() => {
            const macroStepContext = tick.macroStepContext;
            if (!macroStepContext || macroStepContext.ownerMacroStepPath == null) {
                return {
                    ownerMacroStepKey: null,
                    slotInOwner: null,
                    ownerCaseIndex: null,
                };
            }
            const ownerMacroStepKey = resolveStepKeyForTick(macroStepContext.ownerMacroStepPath);
            if (!ownerMacroStepKey) {
                throw new Error(`Cannot convert trace: no mutable macro stepKey found for macroStepContext.ownerMacroStepPath '${macroStepContext.ownerMacroStepPath}' at tick ${tick.tracePointIndex}`);
            }
            if (!isMutableMacroStepKey(ownerMacroStepKey)) {
                throw new Error(`Cannot convert trace: expected macro stepKey for macroStepContext.ownerMacroStepPath '${macroStepContext.ownerMacroStepPath}', got '${ownerMacroStepKey}' at tick ${tick.tracePointIndex}`);
            }
            return {
                ownerMacroStepKey,
                slotInOwner: macroStepContext.slotInOwner,
                ownerCaseIndex: macroStepContext.ownerCaseIndex,
            };
        })();
        const { stepPath: _stepPath, macroStepContext: _macroStepContext, ...tickWithoutStepPath } = tick;
        const mutableTick = {
            ...tickWithoutStepPath,
            stepKey: tickStepKey,
            macroStepContext: mutableMacroStepContext,
            delta: mutableDelta,
        };
        tracePoints.push(mutableTick);
        // Evolve the workflow for the next iteration
        if (convertedInsertedSteps && tick.delta?.stepsMutation?.insertAt !== undefined) {
            const nextStepsByThreadIndex = nextStepsByThreadIndexFromMutation ?? currentMutableWorkflow.stepsByThreadIndex;
            // Apply workflowStateDelta to evolve state
            const nextWorkflowState = { ...currentMutableWorkflow.workflowState };
            if (convertedWorkflowStateDelta) {
                for (const [stepKey, roleEntries] of Object.entries(convertedWorkflowStateDelta)) {
                    nextWorkflowState[stepKey] = {
                        ...(nextWorkflowState[stepKey] ?? {}),
                        ...roleEntries,
                    };
                }
            }
            currentMutableWorkflow = {
                ...currentMutableWorkflow,
                stepsByThreadIndex: nextStepsByThreadIndex,
                workflowState: nextWorkflowState,
            };
        }
        else if (convertedWorkflowStateDelta) {
            // Apply workflowStateDelta even without mutations
            const nextWorkflowState = { ...currentMutableWorkflow.workflowState };
            for (const [stepKey, roleEntries] of Object.entries(convertedWorkflowStateDelta)) {
                nextWorkflowState[stepKey] = {
                    ...(nextWorkflowState[stepKey] ?? {}),
                    ...roleEntries,
                };
            }
            currentMutableWorkflow = {
                ...currentMutableWorkflow,
                workflowState: nextWorkflowState,
            };
        }
    }
    return {
        ...trace,
        tracePoints,
    };
}
